"use client";

import { useEffect, useId, useRef } from "react";
import styles from "./Checkbox.module.css";

/**
 * Square checkbox primitive. Use for multi-selection (e.g. "pick which
 * projects to import"). For boolean state where the meaning is "on/off"
 * (e.g. "Ignore already-imported videos"), prefer Toggle.
 *
 * Supports an `indeterminate` state for select-all headers — when a list
 * has some-but-not-all items selected, render the parent checkbox with
 * `indeterminate={true}`.
 *
 * Props:
 *   - checked: boolean
 *   - indeterminate?: boolean — visual override; semantically still
 *     reports as unchecked to onChange (so a click clears the indeterm.
 *     state in favor of fully-checked).
 *   - onChange: (nextChecked: boolean) => void
 *   - label?: ReactNode — clickable text rendered to the RIGHT of the
 *     box. Pass nothing if you want to wrap the Checkbox yourself.
 *   - disabled?: boolean
 *   - id?: string
 *   - "aria-label"?: string — required when no `label` prop is set
 */
export default function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
  id,
  className,
  "aria-label": ariaLabel,
}) {
  const autoId = useId();
  const inputId = id ?? `cb-${autoId}`;
  const inputRef = useRef(null);

  // The `indeterminate` flag is a DOM property only, not an HTML attribute.
  // Reflect the prop on each render so it stays in sync.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  const boxEl = (
    <span className={styles.box}>
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        className={styles.input}
        checked={!!checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
        aria-checked={indeterminate ? "mixed" : !!checked}
        aria-label={label ? undefined : ariaLabel}
      />
      <span className={styles.indicator} aria-hidden="true" />
    </span>
  );

  if (!label) return boxEl;

  return (
    <label
      htmlFor={inputId}
      className={[
        styles.row,
        disabled ? styles.disabled : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {boxEl}
      <span className={styles.label}>{label}</span>
    </label>
  );
}
